import {
  Card,
  type CardProps,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { ReactElement } from "react";

import { Chip, type ChipProps } from "~/components/ui";
import { formatNumberWithSeparators } from "~/utils/unitConversion";

const CardStyled = styled(Card)(({ theme }) => ({
  minWidth: "192px",
  border: `1px solid ${theme.palette.support[300]}`,
  transition: "all 0.2s ease-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[5],
  },
}));

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { InsightCardProps } from '@monthly/ui' */
export interface InsightCardProps extends CardProps {
  chipIcon?: ReactElement;
  chipText?: string;
  label?: string;
  value?: string | number;
  suffix?: string;
  /**
   * Color of the divider line.
   * @default 'primary.main'
   */
  lineColor?: string;
  /**
   * Color of the value text.
   * @default 'secondary.dark'
   */
  valueColor?: string;
  isPositive?: boolean;
  prefix?: string;
  /**
   * Whether to show the divider line below the value.
   * @default true
   */
  showLine?: boolean;
  /**
   * Whether to show the label below the value.
   * @default true
   */
  showLabel?: boolean;
  chipColor?: ChipProps["color"];
  decimals?: number;
}

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { InsightCard } from '@monthly/ui' */
export function InsightCard({
  label,
  value,
  suffix,
  prefix,
  lineColor,
  valueColor,
  showLine = true,
  showLabel = true,
  chipText,
  chipIcon,
  chipColor,
  decimals = 2,
  ...props
}: InsightCardProps) {
  // Format the display value based on decimals prop
  const formattedValue =
    typeof value === "number"
      ? formatNumberWithSeparators(value, decimals)
      : value;

  return (
    <CardStyled key={label} elevation={2} {...props}>
      <Stack padding="12px 10px" gap={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Typography
            variant="body2"
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightSemiBold,
              color: theme.palette.secondary.dark,
            })}
          >
            {label}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="body1"
              sx={(theme) => ({
                color: theme.palette.secondary.dark,
              })}
            >
              {prefix}
            </Typography>
            <Typography variant="h2" color={valueColor || "secondary.dark"}>
              {formattedValue}
            </Typography>
            <Typography
              variant="body1"
              sx={(theme) => ({
                color: theme.palette.secondary.dark,
              })}
            >
              {suffix}
            </Typography>
          </Stack>
        </Stack>
        {showLabel && (
          <Stack direction="row">
            <Chip
              label={chipText}
              icon={chipIcon}
              color={chipColor}
              size="small"
            />
          </Stack>
        )}
        {showLine && (
          <Divider
            sx={(theme) => ({
              borderColor: lineColor || theme.palette.primary.main,
            })}
          />
        )}
      </Stack>
    </CardStyled>
  );
}
